// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Key Clinical Findings generator.
 *
 * Turns report-period statistics into a prioritized list of clinical findings
 * a vet can act on in ~30 seconds. Each finding answers three questions:
 *   - What happened?        (whatHappened)
 *   - Why does it matter?   (whyItMatters)
 *   - What to review next?  (suggestedReview)
 *
 * Used by BOTH the portal Summary Report (translated via react-admin) and the
 * veterinary PDF report (English, via translateKeyFindingsEn). All copy lives
 * under `pos.key_findings.*` in the i18n files — en.ts is the single source
 * for the English strings the PDF renders.
 *
 * IMPORTANT: findings must never include insulin dose recommendations — they
 * prompt review only (acceptance requirement).
 */

import { format } from 'date-fns'
import { formatGlucoseForDisplay } from '../common'
import englishMessages from '../i18n/en'
import { GLUCOSE_THRESHOLDS } from '../types/dashboard'

export type KeyFindingSeverity = 'high' | 'medium' | 'info'

/** Presentation grouping for the redesigned Key CGM Data Insights cards (portal). */
export type KeyFindingGroup = 'needs-review' | 'monitoring' | 'data-quality'

export interface KeyFindingsInput {
  startDate: Date
  endDate: Date
  /** Display unit ('mmol/L' | 'mg/dL'); all glucose inputs are mmol/L */
  glucoseUnit: string
  /** Species target range, mmol/L */
  targetMin: number
  targetMax: number
  /**
   * Upper bound of the High zone (above this = Very High), mmol/L — dogs 22.2,
   * cats 27.8. MUST match the boundary calculateTIRStats bins with, or a peak
   * is flagged "very high" here while the TIR capsule counts it as merely High
   * (eng-brain P13-1 / L-021). Falls back to GLUCOSE_THRESHOLDS.VERY_HIGH.
   */
  highMax?: number
  /** Percentages 0-100, computed over the SAME pipeline the charts use */
  tir: number
  tar: number
  tbr: number
  cv: number
  /** mmol/L */
  stdDev: number
  avgGlucose: number
  /** Percentage 0-100 */
  dataCoverage: number
  readingsCount: number
  /** Readings below the target range but >= the clinical very-low floor */
  lowReadingCount: number
  /** Readings below GLUCOSE_THRESHOLDS.VERY_LOW (3.0 mmol/L) */
  veryLowReadingCount: number
  /**
   * Owner-recorded events (insulin/meal/medication/symptom/activity) in period.
   * null = UNKNOWN (the count failed to load): never reported as "missing".
   */
  ownerEventCount: number | null
  /** mmol/L; null/undefined when not available */
  peakGlucose?: number | null
  peakTimestampMs?: number | null
  nadirGlucose?: number | null
  nadirTimestampMs?: number | null
  /** Active (effective) data days in the period — used by the data-quality card */
  activeDataDays?: number
  /** Patient name for the Selected Period Data Summary lead sentence */
  petName?: string
  /** 'pdf' references report pages; 'portal' references on-screen sections */
  surface?: 'portal' | 'pdf'
  /** Cap on findings returned (default 6 portal / pass 5 for the PDF) */
  maxFindings?: number
}

export interface KeyFinding {
  /** Stable finding-type id, e.g. 'tar', 'owner_events_missing' */
  id: string
  severity: KeyFindingSeverity
  title: string
  whatHappened: string
  whyItMatters: string
  suggestedReview: string
  relatedSection: string
  /** Lower renders first; encodes the risk-first priority buckets */
  displayOrder: number

  // --- Redesigned "Key CGM Data Insights" card fields (portal only). These are
  // populated for the on-screen Summary Report. The PDF ignores them and keeps
  // using the prose fields above. ---
  /** Card grouping column: Needs Review / Monitoring Context / Data Quality */
  group?: KeyFindingGroup
  /** Badge severity for the card (data-descriptive; may differ from `severity`) */
  displaySeverity?: KeyFindingSeverity
  /** Card title (data-descriptive wording; differs from the PDF `title`) */
  cardTitle?: string
  /** Headline value, e.g. '36.7%', '>540', '0' */
  keyValue?: string
  /**
   * Unit word rendered next to `keyValue` when the headline is a COUNT rather
   * than a glucose value — 'readings'. Without it the low-glucose cards put a
   * bare number in the same slot the peak card uses for a glucose value, and a
   * vet reads "311 / Readings below 79 mg/dL" as a reading of 311 mg/dL
   * (Faithfull Friends, 2026-08-18). Cards whose headline IS a value leave it
   * unset.
   */
  keyValueSuffix?: string
  /** Label under the headline value */
  keyValueLabel?: string
  /** Optional secondary value, e.g. 'Jun 8, 07:36' or '148' (SD) */
  subValue?: string
  /** Label under the secondary value */
  subValueLabel?: string
  /** Card "what it means" paragraph */
  meaning?: string
  /** "For Veterinary Review" card content (empty string hides the box) */
  cardReview?: string
  /** Short "See:" link text for the card (empty string hides the link) */
  seeLink?: string
}

export type KeyFindingsTranslate = (key: string, params?: Record<string, unknown>) => string

const MAX_FINDINGS_DEFAULT = 6

// Display-order buckets (risk-first, per PRD):
//   safety events < data-quality risks < owner context < glucose patterns
//   < key values < reassurance / info
const ORDER = {
  LOW_GLUCOSE: 10,
  DATA_QUALITY_POOR: 20,
  DATA_QUALITY_MODERATE: 25,
  OWNER_EVENTS_MISSING: 30,
  TAR: 40,
  TIR: 50,
  VARIABILITY: 60,
  PEAK_NADIR: 70,
  NO_LOW_EVENTS: 80,
  DATA_QUALITY_ADEQUATE: 90,
} as const

function resolvePath(obj: unknown, key: string): unknown {
  return key.split('.').reduce<unknown>((o, part) => {
    if (o == null || typeof o !== 'object') return undefined
    return (o as Record<string, unknown>)[part]
  }, obj)
}

/**
 * English resolver for non-React contexts (the PDF exporter). Resolves the
 * same `pos.key_findings.*` keys from en.ts with polyglot-style %{param}
 * interpolation, so PDF copy and portal copy share one source.
 */
export const translateKeyFindingsEn: KeyFindingsTranslate = (key, params = {}) => {
  const template = resolvePath(englishMessages, key)
  if (typeof template !== 'string') return key
  return template.replace(/%\{(\w+)\}/g, (_, name: string) => String(params[name] ?? ''))
}

interface Candidate extends KeyFinding {
  /** Required findings survive the maxFindings cap (safety/data-quality/owner) */
  required: boolean
}

/** Fields the redesigned insights cards add on top of a base finding. */
type InsightsFields = Pick<
  KeyFinding,
  | 'group'
  | 'displaySeverity'
  | 'cardTitle'
  | 'keyValue'
  | 'keyValueSuffix'
  | 'keyValueLabel'
  | 'subValue'
  | 'subValueLabel'
  | 'meaning'
  | 'cardReview'
  | 'seeLink'
>

/** Drop a leading clamp marker ('>'/'<') so it reads naturally in a title. */
function stripClamp(value: string): string {
  return value.replace(/^[<>]/, '')
}

/**
 * Derive the Key CGM Data Insights card presentation for a finding from the
 * `pos.cgm_insights.*` namespace and computed period stats. Returns {} for
 * finding ids that have no card form (they won't render in the portal panel).
 */
function decorateInsights(
  finding: KeyFinding,
  input: KeyFindingsInput,
  t: KeyFindingsTranslate,
  fmt: (mmol: number) => string,
): InsightsFields {
  const K = 'pos.cgm_insights'
  const unit = input.glucoseUnit
  const timeOf = (ms: number | null | undefined) =>
    ms != null ? format(new Date(ms), 'MMM d, HH:mm') : undefined

  switch (finding.id) {
    case 'tar':
      return {
        group: 'needs-review',
        displaySeverity: 'medium',
        cardTitle: t(`${K}.tar.title`),
        keyValue: `${input.tar.toFixed(1)}%`,
        keyValueLabel: t(`${K}.tar.key_label`),
        meaning: t(`${K}.tar.meaning`, { tar: input.tar.toFixed(1) }),
        cardReview: t(`${K}.tar.review`),
        seeLink: t(`${K}.tar.see`),
      }
    case 'peak_nadir': {
      const peak = input.peakGlucose
      const peakStr = peak != null ? fmt(peak) : '—'
      const isHigh = peak != null && peak >= (input.highMax ?? GLUCOSE_THRESHOLDS.VERY_HIGH)
      return {
        group: 'needs-review',
        displaySeverity: isHigh ? 'high' : 'info',
        cardTitle: t(`${K}.peak.title`, { peak: stripClamp(peakStr), unit }),
        keyValue: peakStr,
        keyValueLabel: t(`${K}.peak.key_label`, { unit }),
        subValue: timeOf(input.peakTimestampMs),
        subValueLabel: t(`${K}.peak.sub_label`),
        meaning: t(`${K}.peak.meaning`),
        cardReview: t(`${K}.peak.review`),
        seeLink: t(`${K}.peak.see`),
      }
    }
    case 'variability':
      return {
        group: 'needs-review',
        displaySeverity: 'medium',
        cardTitle: t(`${K}.variability.title`),
        keyValue: `${input.cv.toFixed(0)}%`,
        keyValueLabel: t(`${K}.variability.key_label`),
        subValue: fmt(input.stdDev),
        subValueLabel: t(`${K}.variability.sub_label`),
        meaning: t(`${K}.variability.meaning`),
        cardReview: t(`${K}.variability.review`),
        seeLink: t(`${K}.variability.see`),
      }
    case 'low_glucose': {
      // The counted band and the labelled band must be the SAME band: showing
      // the very-low count under a target-minimum label understated Reuben's
      // low burden 39-vs-348 (2026-08-18).
      const showVeryLow = input.veryLowReadingCount > 0
      const count = showVeryLow ? input.veryLowReadingCount : input.lowReadingCount
      const band = showVeryLow ? GLUCOSE_THRESHOLDS.VERY_LOW : input.targetMin
      return {
        group: 'needs-review',
        displaySeverity: 'high',
        cardTitle: t(`${K}.low_glucose.title`),
        keyValue: String(count),
        keyValueSuffix: t(`${K}.readings_suffix`),
        keyValueLabel: t(`${K}.low_glucose.key_label`, { threshold: fmt(band), unit }),
        meaning: t(`${K}.low_glucose.meaning`),
        cardReview: t(`${K}.low_glucose.review`),
        seeLink: t(`${K}.low_glucose.see`),
      }
    }
    case 'owner_events_missing':
      return {
        group: 'needs-review',
        displaySeverity: 'high',
        cardTitle: t(`${K}.owner_events_missing.title`),
        keyValue: '0',
        keyValueLabel: t(`${K}.owner_events_missing.key_label`),
        meaning: t(`${K}.owner_events_missing.meaning`),
        cardReview: t(`${K}.owner_events_missing.review`),
        seeLink: t(`${K}.owner_events_missing.see`),
      }
    case 'tir':
      return {
        group: 'monitoring',
        displaySeverity: 'info',
        cardTitle: t(`${K}.tir.title`),
        keyValue: `${input.tir.toFixed(1)}%`,
        keyValueLabel: t(`${K}.tir.key_label`, {
          min: fmt(input.targetMin),
          max: fmt(input.targetMax),
          unit,
        }),
        meaning: t(`${K}.tir.meaning`),
        cardReview: t(`${K}.tir.review`),
        seeLink: t(`${K}.tir.see`),
      }
    case 'no_low_events':
      return {
        group: 'monitoring',
        displaySeverity: 'info',
        cardTitle: t(`${K}.no_low_events.title`),
        keyValue: '0',
        keyValueSuffix: t(`${K}.readings_suffix`),
        keyValueLabel: t(`${K}.no_low_events.key_label`, { threshold: fmt(input.targetMin), unit }),
        meaning: t(`${K}.no_low_events.meaning`, { threshold: fmt(input.targetMin), unit }),
        cardReview: t(`${K}.no_low_events.review`),
        seeLink: t(`${K}.no_low_events.see`),
      }
    case 'data_quality': {
      const adequate = input.dataCoverage >= 80
      return {
        group: 'data-quality',
        displaySeverity: 'info',
        cardTitle: t(`${K}.data_quality.${adequate ? 'title' : 'title_limited'}`),
        keyValue: `${input.dataCoverage.toFixed(0)}%`,
        keyValueLabel: t(`${K}.data_quality.key_label`, { readings: input.readingsCount }),
        subValue:
          input.activeDataDays != null
            ? t(`${K}.data_quality.days_value`, { days: input.activeDataDays })
            : undefined,
        subValueLabel: input.activeDataDays != null ? t(`${K}.data_quality.sub_label`) : undefined,
        meaning: t(`${K}.data_quality.meaning`),
        cardReview: '',
        seeLink: '',
      }
    }
    default:
      return {}
  }
}

/** Join clauses as "a, b, and c" (no comma for two items). */
function joinClauses(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

/**
 * Build the "Selected Period Data Summary" paragraph (the amber box) from the
 * same period stats, in data-descriptive language. Used by the portal only.
 */
export function generateInsightsSummary(
  input: KeyFindingsInput,
  t: KeyFindingsTranslate = translateKeyFindingsEn,
): string {
  const K = 'pos.cgm_insights.summary'
  const unit = input.glucoseUnit
  const fmt = (mmol: number) => formatGlucoseForDisplay(mmol, unit, { withUnit: false })

  const clauses: string[] = []
  if (input.tar >= 20) clauses.push(t(`${K}.${input.tar > 40 ? 'tar_high' : 'tar_moderate'}`))
  if (input.cv > 35) clauses.push(t(`${K}.variability_high`))
  if (
    input.peakGlucose != null &&
    input.peakGlucose >= (input.highMax ?? GLUCOSE_THRESHOLDS.VERY_HIGH)
  ) {
    clauses.push(t(`${K}.peak`, { peak: stripClamp(fmt(input.peakGlucose)), unit }))
  }

  let para = ''
  if (clauses.length) {
    const lead = input.petName ? t(`${K}.lead`, { pet: input.petName }) : t(`${K}.lead_generic`)
    para += `${lead}${joinClauses(clauses)}. `
  }

  const hasLow = input.veryLowReadingCount > 0 || input.lowReadingCount > 0 || input.tbr > 0
  para += hasLow
    ? `${t(`${K}.low`, {
        count: input.veryLowReadingCount || input.lowReadingCount,
        threshold: fmt(input.targetMin),
        unit,
      })} `
    : `${t(`${K}.no_low`)} `

  const adequate = input.dataCoverage >= 80
  para += t(`${K}.${adequate ? 'coverage_adequate' : 'coverage_limited'}`)
  const daysLimited = input.activeDataDays != null && input.activeDataDays < 3
  para += daysLimited ? t(`${K}.days_limited`, { days: input.activeDataDays }) : t(`${K}.days_ok`)

  return para.trim()
}

export function generateKeyFindings(
  input: KeyFindingsInput,
  t: KeyFindingsTranslate = translateKeyFindingsEn,
): KeyFinding[] {
  const K = 'pos.key_findings'
  const unit = input.glucoseUnit
  const fmt = (mmol: number) => formatGlucoseForDisplay(mmol, unit, { withUnit: false })
  const surface = input.surface ?? 'portal'
  const related = (id: string) => t(`${K}.${id}.${surface === 'pdf' ? 'related_pdf' : 'related'}`)

  const candidates: Candidate[] = []

  // --- Low glucose safety (always generated — acceptance: the safety
  // statement must appear even when no lows were detected) ---
  if (input.veryLowReadingCount > 0) {
    candidates.push({
      id: 'low_glucose',
      severity: 'high',
      displayOrder: ORDER.LOW_GLUCOSE,
      required: true,
      title: t(`${K}.low_glucose.title_significant`),
      whatHappened: t(`${K}.low_glucose.what_significant`, {
        count: input.veryLowReadingCount,
        threshold: fmt(GLUCOSE_THRESHOLDS.VERY_LOW),
        unit,
        tbr: input.tbr.toFixed(1),
      }),
      whyItMatters: t(`${K}.low_glucose.why`),
      suggestedReview: t(`${K}.low_glucose.review`),
      relatedSection: related('low_glucose'),
    })
  } else if (input.tbr > 0 || input.lowReadingCount > 0) {
    candidates.push({
      id: 'low_glucose',
      severity: 'high',
      displayOrder: ORDER.LOW_GLUCOSE,
      required: true,
      title: t(`${K}.low_glucose.title_detected`),
      whatHappened: t(`${K}.low_glucose.what_detected`, {
        threshold: fmt(input.targetMin),
        unit,
        tbr: input.tbr.toFixed(1),
      }),
      whyItMatters: t(`${K}.low_glucose.why`),
      suggestedReview: t(`${K}.low_glucose.review`),
      relatedSection: related('low_glucose'),
    })
  } else {
    // TBR = 0: reassurance with a mandatory safety boundary — never promise
    // the absence of hypoglycemia beyond the recorded data.
    candidates.push({
      id: 'no_low_events',
      severity: 'medium',
      displayOrder: ORDER.NO_LOW_EVENTS,
      required: true,
      title: t(`${K}.no_low_events.title`),
      whatHappened: t(`${K}.no_low_events.what`, { threshold: fmt(input.targetMin), unit }),
      whyItMatters: t(`${K}.no_low_events.why`),
      suggestedReview: t(`${K}.no_low_events.review`),
      relatedSection: related('no_low_events'),
    })
  }

  // --- Data quality (always generated; tier by coverage) ---
  const coverage = input.dataCoverage
  const coverageTier =
    coverage >= 80 ? 'adequate' : coverage >= 50 ? 'moderate' : coverage >= 25 ? 'limited' : 'poor'
  const coverageOrder =
    coverageTier === 'adequate'
      ? ORDER.DATA_QUALITY_ADEQUATE
      : coverageTier === 'moderate'
        ? ORDER.DATA_QUALITY_MODERATE
        : ORDER.DATA_QUALITY_POOR
  candidates.push({
    id: 'data_quality',
    severity:
      coverageTier === 'adequate' ? 'info' : coverageTier === 'moderate' ? 'medium' : 'high',
    displayOrder: coverageOrder,
    required: true,
    title: t(`${K}.data_quality.title_${coverageTier}`),
    whatHappened: t(`${K}.data_quality.what`, {
      coverage: coverage.toFixed(0),
      start: format(input.startDate, 'MMM d, yyyy'),
      end: format(input.endDate, 'MMM d, yyyy'),
      readings: input.readingsCount,
    }),
    whyItMatters: t(`${K}.data_quality.why_${coverageTier === 'adequate' ? 'adequate' : 'low'}`),
    suggestedReview: t(
      `${K}.data_quality.review_${coverageTier === 'adequate' ? 'adequate' : 'low'}`,
    ),
    relatedSection: related('data_quality'),
  })

  // --- Owner event context (warning is mandatory when nothing was recorded) ---
  const ownerEventsMissing = input.ownerEventCount === 0
  if (ownerEventsMissing) {
    candidates.push({
      id: 'owner_events_missing',
      severity: 'high',
      displayOrder: ORDER.OWNER_EVENTS_MISSING,
      required: true,
      title: t(`${K}.owner_events_missing.title`),
      whatHappened: t(`${K}.owner_events_missing.what`),
      whyItMatters: t(`${K}.owner_events_missing.why`),
      suggestedReview: t(`${K}.owner_events_missing.review`),
      relatedSection: related('owner_events_missing'),
    })
  }

  // --- Above-range exposure (only when clinically notable: TAR >= 20%) ---
  if (input.tar >= 20) {
    const tarTier = input.tar > 60 ? 'predominant' : input.tar > 40 ? 'elevated' : 'moderate'
    candidates.push({
      id: 'tar',
      severity: tarTier === 'moderate' ? 'medium' : 'high',
      displayOrder: ORDER.TAR,
      required: false,
      title: t(`${K}.tar.title_${tarTier}`),
      whatHappened: t(`${K}.tar.what`, { tar: input.tar.toFixed(1) }),
      whyItMatters: t(`${K}.tar.why`),
      suggestedReview: t(`${K}.tar.review`),
      relatedSection: related('tar'),
    })
  }

  // --- Time in target range (always a candidate; tiered wording) ---
  const tirTier =
    input.tir >= 70
      ? 'majority'
      : input.tir >= 50
        ? 'moderate'
        : input.tir >= 25
          ? 'limited'
          : 'very_limited'
  candidates.push({
    id: 'tir',
    severity: tirTier === 'very_limited' ? 'high' : tirTier === 'limited' ? 'medium' : 'info',
    displayOrder: ORDER.TIR,
    required: false,
    title: t(`${K}.tir.title_${tirTier}`),
    whatHappened: t(`${K}.tir.what`, {
      tir: input.tir.toFixed(1),
      min: fmt(input.targetMin),
      max: fmt(input.targetMax),
      unit,
    }),
    whyItMatters: t(`${K}.tir.why`),
    suggestedReview: t(`${K}.tir.review`),
    relatedSection: related('tir'),
  })

  // --- Glucose variability (only when high: CV > 35%) ---
  if (input.cv > 35) {
    candidates.push({
      id: 'variability',
      severity: 'medium',
      displayOrder: ORDER.VARIABILITY,
      required: false,
      title: t(`${K}.variability.title`),
      whatHappened: t(`${K}.variability.what`, {
        cv: input.cv.toFixed(0),
        sd: fmt(input.stdDev),
        unit,
      }),
      whyItMatters: t(`${K}.variability.why`),
      suggestedReview: t(`${K}.variability.review`),
      relatedSection: related('variability'),
    })
  }

  // --- Peak / nadir key values ---
  if (
    input.peakGlucose != null &&
    input.nadirGlucose != null &&
    Number.isFinite(input.peakGlucose) &&
    Number.isFinite(input.nadirGlucose)
  ) {
    const timeOf = (ms: number | null | undefined) =>
      ms != null ? format(new Date(ms), 'MMM d, HH:mm') : '—'
    const why = ownerEventsMissing
      ? `${t(`${K}.peak_nadir.why`)} ${t(`${K}.peak_nadir.why_no_events`)}`
      : t(`${K}.peak_nadir.why`)
    candidates.push({
      id: 'peak_nadir',
      severity: 'info',
      displayOrder: ORDER.PEAK_NADIR,
      required: false,
      title: t(`${K}.peak_nadir.title`, { peak: fmt(input.peakGlucose), unit }),
      whatHappened: t(`${K}.peak_nadir.what`, {
        peak: fmt(input.peakGlucose),
        nadir: fmt(input.nadirGlucose),
        unit,
        peakTime: timeOf(input.peakTimestampMs),
        nadirTime: timeOf(input.nadirTimestampMs),
      }),
      whyItMatters: why,
      suggestedReview: t(`${K}.peak_nadir.review`),
      relatedSection: related('peak_nadir'),
    })
  }

  // --- Selection: required findings always survive; fill the rest by
  // priority until the cap, then sort risk-first for display ---
  const max = Math.max(1, input.maxFindings ?? MAX_FINDINGS_DEFAULT)
  const selected: Candidate[] = candidates.filter((c) => c.required)
  const optional = candidates
    .filter((c) => !c.required)
    .sort((a, b) => a.displayOrder - b.displayOrder)
  for (const candidate of optional) {
    if (selected.length >= max) break
    selected.push(candidate)
  }

  return selected
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(({ required: _required, ...finding }) => ({
      ...finding,
      ...decorateInsights(finding, input, t, fmt),
    }))
}
